/**
 * Add to Cart Button Manager
 * Handles button state for products with Size/Width options
 */

const SIZE_RADIO_SELECTOR = '.custom-option-buttons[data-opt-name="Size"], .custom-option-buttons[data-opt-name="Width"]';
const HAS_VARIANT_PARAM = new URLSearchParams(window.location.search).has('variant');

class AddToCartButtonManager {
  constructor() {
    this.mainButton = null;
    this.stickyButton = null;
    this.hasSizeOption = false;
    this.init();
  }

  getSizeRadios() {
    return document.querySelectorAll(SIZE_RADIO_SELECTOR);
  }

  getSelectedSizeControl() {
    return document.querySelector(
      '.custom-option-buttons[data-opt-name="Size"]:checked, .custom-option-buttons[data-opt-name="Width"]:checked'
    );
  }

  clearSizeSelection() {
    this.getSizeRadios().forEach((option) => {
      option.checked = false;
      option.removeAttribute('checked');

      const label = option.nextElementSibling;
      if (label && label.classList.contains('label-swatch')) {
        label.classList.remove('checked');
      }

      const labelById = document.querySelector(`label[for="${option.id}"]`);
      if (labelById) {
        labelById.classList.remove('checked');
      }
    });

    // Unchecking a radio in code fires no change event, so the mobile dropdown has to be
    // told to drop the value it read from the server before this ran.
    document.querySelectorAll('size-dropdown').forEach((dropdown) => dropdown.syncFromRadios?.());
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.mainButton = document.querySelector('.product-form__submit');
    this.stickyButton = document.querySelector('.sticky_form__submit');
    
    if (!this.mainButton) return;
    
    // Check if product has size/width option
    this.hasSizeOption = this.mainButton.getAttribute('data-has-size-option') === 'true';
    
    if (this.hasSizeOption && !HAS_VARIANT_PARAM) {
      this.clearSizeSelection();

      this.setButtonText('PLEASE SELECT SIZE');
      this.disableButton();
      this.hidePrice();
    }

    // Listen for variant changes
    this.attachVariantListeners();
    this.updateButtonState();

    // buy-buttons.liquid renders the label and price empty for products with a size
    // option, so the gate has to run again after every section re-render rather than
    // on a timer, otherwise the button can be left without its price.
    const pubsub = window.theme?.pubsub;
    pubsub?.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, () => this.refresh());

    // Listen for product switch events (from color swatch handler)
    document.addEventListener('product:switched', () => {
      setTimeout(() => this.handleProductSwitch(), 100);
    });
  }

  handleProductSwitch() {
    // Re-query buttons after product switch
    this.mainButton = document.querySelector('.product-form__submit');
    this.stickyButton = document.querySelector('.sticky_form__submit');
    
    if (!this.mainButton) return;
    
    this.hasSizeOption = this.mainButton.getAttribute('data-has-size-option') === 'true';
    
    if (this.hasSizeOption) {
      this.clearSizeSelection();

      this.setButtonText('PLEASE SELECT SIZE');
      this.disableButton();
      this.hidePrice();
    } else {
      // No size option, show default state
      this.updateButtonState();
    }
    
    // Re-attach variant listeners
    this.attachVariantListeners();
  }

  attachVariantListeners() {
    const onSizeChange = () => {
      setTimeout(() => this.updateButtonState(), 50);
    };

    const bind = (element) => {
      if (element.dataset.sizeGateBound === 'true') return;
      element.dataset.sizeGateBound = 'true';
      element.addEventListener('change', onSizeChange);
    };

    this.getSizeRadios().forEach(bind);
  }

  refresh() {
    this.mainButton = document.querySelector('.product-form__submit');
    this.stickyButton = document.querySelector('.sticky_form__submit');

    if (!this.mainButton) return;

    this.hasSizeOption = this.mainButton.getAttribute('data-has-size-option') === 'true';

    this.attachVariantListeners();
    this.updateButtonState();
  }

  updateButtonState() {
    if (!this.hasSizeOption) {
      // No size option, keep default state
      return;
    }

    // Once a size is picked the section re-render is authoritative for the
    // button label, price and availability, so only the unpicked state is forced here.
    if (this.getSelectedSizeControl()) {
      this.restoreDefaultLabel();
      this.restorePrice();
      return;
    }

    this.setButtonText('PLEASE SELECT SIZE');
    this.disableButton();
    this.hidePrice();
  }

  checkVariantAndUpdateButton(variantId) {
    // Get variant data from the page's JSON
    const variantData = this.getVariantData(variantId);
    
    if (variantData) {
      if (variantData.available) {
        this.setButtonText('Add to Cart');
        this.enableButton();
        this.showPrice(variantData.price);
      } else {
        this.setButtonText('Sold out');
        this.disableButton();
        this.hidePrice();
      }
    }
  }

  getVariantData(variantId) {
    // Try to get variant data from product JSON script tag
    const productJson = document.querySelector('script[type="application/json"][data-product-json]');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        return product.variants.find(v => v.id == variantId);
      } catch (e) {
        // Error parsing product JSON
      }
    }
    return null;
  }

  setButtonText(text) {
    if (this.mainButton) {
      const btnText = this.mainButton.querySelector('.btn-text');
      if (btnText) {
        let textSpan = btnText.querySelector('span:first-child');
        if (textSpan) {
          textSpan.textContent = text;
        } else {
          // Remove all text nodes
          Array.from(btnText.childNodes).forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              node.remove();
            }
          });
          
          textSpan = document.createElement('span');
          textSpan.textContent = text;
          // Insert at the beginning, before price div
          btnText.insertBefore(textSpan, btnText.firstChild);
        }
      }
    }
    
    if (this.stickyButton) {
      const btnText = this.stickyButton.querySelector('.btn-text');
      if (btnText) {
        const textSpan = btnText.querySelector('span.hidden');
        if (textSpan) {
          textSpan.textContent = text;
        } else {
          // Try without the .hidden class
          const anySpan = btnText.querySelector('span:not([class*="icon"])');
          if (anySpan) {
            anySpan.textContent = text;
          }
        }
      }
    }
  }

  enableButton() {
    if (this.mainButton) {
      this.mainButton.disabled = false;
      this.mainButton.removeAttribute('disabled');
    }
    
    if (this.stickyButton) {
      this.stickyButton.classList.remove('disabled-btn');
    }
  }

  disableButton() {
    if (this.mainButton) {
      this.mainButton.disabled = true;
      this.mainButton.setAttribute('disabled', 'disabled');
    }
    
    if (this.stickyButton) {
      this.stickyButton.classList.add('disabled-btn');
    }
  }

  showPrice(price) {
    if (this.mainButton) {
      const btnText = this.mainButton.querySelector('.btn-text');
      if (!btnText) return;
      
      // Look for any price div (with or without ID)
      let priceDiv = btnText.querySelector('[id^="BuyButtonPrice-"]') || btnText.querySelector('div.flex');
      
      if (!priceDiv) {
        // Create price div if it doesn't exist
        priceDiv = document.createElement('div');
        priceDiv.className = 'flex';
        priceDiv.style.display = 'flex';
        
        const separator = document.createElement('span');
        separator.className = 'relative';
        separator.innerHTML = '&nbsp;&nbsp;-&nbsp;&nbsp;';
        
        const priceElement = document.createElement('product-buy-price');
        priceElement.className = 'price';
        
        priceDiv.appendChild(separator);
        priceDiv.appendChild(priceElement);
        btnText.appendChild(priceDiv);
      } else {
        // Remove any duplicate price divs
        const allPriceDivs = btnText.querySelectorAll('div.flex');
        if (allPriceDivs.length > 1) {
          allPriceDivs.forEach((div, index) => {
            if (index > 0) {
              div.remove();
            }
          });
        }
        priceDiv.style.display = 'flex';
      }
      
      const priceElement = priceDiv.querySelector('product-buy-price');
      
      if (priceElement && price) {
        priceElement.setAttribute('data-price', price);
        // Format price (assuming Shopify money format)
        const formattedPrice = this.formatMoney(price);
        priceElement.textContent = formattedPrice;
      }
    }
  }

  hidePrice() {
    if (!this.mainButton) return;

    const priceDiv = this.mainButton.querySelector('[id^="BuyButtonPrice-"]');
    if (priceDiv) {
      priceDiv.style.display = 'none';
    }

    const fallback = this.mainButton.querySelector('[data-fallback-price]');
    if (fallback) {
      fallback.style.display = 'none';
    }
  }

  restoreDefaultLabel() {
    if (!this.mainButton) return;

    // buy-buttons.liquid renders this label empty for products with a size option and
    // leaves it to this script, so it is filled from the server computed default only
    // when nothing else has written to it.
    const label = this.mainButton.querySelector('.btn-text span[data-default-text]');
    if (label && label.textContent.trim() === '') {
      label.textContent = label.getAttribute('data-default-text');
    }
  }

  restorePrice() {
    if (!this.mainButton) return;

    // After a colour switch the section re-render lags behind the click, so the price is
    // held back until the label stops asking for a size and the two agree. The label loses
    // its data attribute in that flow, so the first span is what has to be read here.
    const label = this.mainButton.querySelector('.btn-text > span:first-child');
    if (label && label.textContent.trim() === 'PLEASE SELECT SIZE') return;

    const priceDiv = this.mainButton.querySelector('[id^="BuyButtonPrice-"]');
    if (priceDiv) {
      // The theme marks the price [hidden] for unavailable variants, but the published
      // behaviour keeps it visible next to the label, so the display is forced here.
      priceDiv.style.display = 'flex';
      return;
    }

    // Switching colour makes the theme replace the whole button content, which drops the
    // server rendered price, so it is rebuilt here from the selected variant.
    this.renderFallbackPrice();
  }

  renderFallbackPrice() {
    const btnText = this.mainButton.querySelector('.btn-text');
    if (!btnText) return;

    const formatted = this.getFormattedVariantPrice();
    if (!formatted) return;

    let holder = btnText.querySelector('[data-fallback-price]');
    if (!holder) {
      holder = document.createElement('div');
      holder.className = 'flex';
      holder.setAttribute('data-fallback-price', '');

      const separator = document.createElement('span');
      separator.className = 'relative';
      separator.innerHTML = '&nbsp;&nbsp;-&nbsp;&nbsp;';

      const amount = document.createElement('span');
      amount.className = 'price';

      holder.appendChild(separator);
      holder.appendChild(amount);
      btnText.appendChild(holder);
    }

    holder.style.display = 'flex';
    holder.querySelector('.price').textContent = formatted;
  }

  getFormattedVariantPrice() {
    const script = document.querySelector('script[type="application/json"][data-selected-variant]');
    const currency = window.theme?.Currency;
    const settings = window.theme?.settings;
    if (!script || !currency || !settings) return null;

    try {
      const variant = JSON.parse(script.textContent);
      if (typeof variant?.price !== 'number') return null;

      // Same format lookup the theme uses elsewhere, so it follows the market the shopper
      // is browsing in rather than assuming a currency.
      const format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
      return currency.formatMoney(variant.price, format);
    } catch (error) {
      return null;
    }
  }

  formatMoney(cents) {
    // Simple money formatting - adjust based on your currency settings
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize
window.addToCartButtonManager = new AddToCartButtonManager();
